import { useDebouncedCallback, useThrottledCallback } from "@mantine/hooks";
import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";

import { useAgentSdk, useChatStore, getSessionByTaskId } from "@/sdk";
import { type ChatTask, useIsSendingChatMessage, useSendChatMessage } from "@/sdk/agent/chat";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useFeatureFlag } from "@/shared-utils";

import type { SpellCheckMetadataRef } from "./spellcheck/types";

import { type ScrollAreaViewport, scrollToBottom } from "../ChatMessages/scrollUtils";
import { MessageInputSection } from "./MessageInputSection";
import { SPELLCHECK_CONFIG } from "./spellcheck/config";
import { useSpellCheck } from "./spellcheck/useSpellCheck";

const containsFourDigits = (input: string) => /(?:\d\D*){4,}/.test(input);

export interface MessageInputProps {
    chatTask: ChatTask & { customerName: { value?: string; error?: boolean } };
    scrollViewportRef: ScrollAreaViewport;
    /** Required for component identity and more efficient updates */
    chatId: string;
}

export function MessageInput({ chatTask, scrollViewportRef, chatId }: MessageInputProps) {
    const logger = useLogger({
        module: "MessageInput",
        supportTeam: "messaging",
    });
    const agentSdk = useAgentSdk();
    const { partner } = chatTask;
    const session = getSessionByTaskId(chatTask.id);
    const { data: isSpellCheckEnabled = false } = useFeatureFlag<boolean>("EWP-chat-spellCheckEnabled", { partner });

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const input = useChatStore((state) => state.inputs.get(chatId) ?? "");
    const setInput = useChatStore((state) => state.setInput);
    const [localInput, setLocalInput] = useState(input);

    const debouncedSetInput = useDebouncedCallback((value: string) => {
        setInput(chatId, value);
    }, 300);

    const containsPotentialPii = containsFourDigits(localInput);
    const isSendingMessage = useIsSendingChatMessage(chatId);

    const spellCheckMetadataRef = useRef<SpellCheckMetadataRef>({
        spellcheckEnabled: isSpellCheckEnabled,
    });

    const onCancelWithText = useCallback(
        (correctedText: string) => {
            setLocalInput(correctedText);
            debouncedSetInput(correctedText);
            requestAnimationFrame(() => textareaRef.current?.focus());
        },
        [debouncedSetInput],
    );

    // Placeholder sendMessage - will be replaced after useSendChatMessage is initialized
    const sendMessageRef = useRef<(text: string) => void>(() => {});

    const sessionId = session?.id ?? "";
    const spellCheckEnabled = isSpellCheckEnabled && !!sessionId;

    const spellCheck = useSpellCheck({
        chatId,
        sessionId,
        partner,
        enabled: spellCheckEnabled,
        config: SPELLCHECK_CONFIG,
        localInput,
        sendMessage: (text) => sendMessageRef.current(text),
        spellCheckMetadataRef,
        onCancelWithText,
    });

    const { sendChatMessage } = useSendChatMessage({
        chatTask,
        chatId,
        sourceComponent: "message-input",
        extraMetadata: spellCheckMetadataRef.current as unknown as Record<string, unknown>,
        onBeforeSend: () => {
            setLocalInput("");
            debouncedSetInput("");
            spellCheck.clearSpellCheck();
            scrollToBottom(scrollViewportRef);
            requestAnimationFrame(() => textareaRef.current?.focus());
        },
    });

    // Update the ref so spellCheck handlers can call sendMessage
    sendMessageRef.current = (text: string) => void sendChatMessage(text.trim());

    const isSendDisabled =
        isSendingMessage ||
        chatTask.status !== "assigned" ||
        localInput.trimEnd() === "" ||
        spellCheck.displayStatus === "suggestions" ||
        spellCheck.displayStatus === "checking";

    const isInputDisabled = chatTask.status !== "assigned" || spellCheck.displayStatus === "suggestions";

    // Scroll to bottom when spellcheck checking or suggestions appear so they're visible
    useEffect(() => {
        if (spellCheck.displayStatus === "checking" || spellCheck.displayStatus === "suggestions") {
            requestAnimationFrame(() => scrollToBottom(scrollViewportRef));
        }
    }, [spellCheck.displayStatus, scrollViewportRef]);

    const throttledSendTyping = useThrottledCallback(() => {
        void agentSdk.sendTypingIndicator(chatTask).catch((error) => {
            logger.trace({ error, chatId }, "Failed to send typing indicator (non-critical)");
        });
    }, 3000);

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            const inputValue = event.currentTarget.value;
            setLocalInput(inputValue);
            debouncedSetInput(inputValue);

            if (spellCheckEnabled && inputValue?.length > 0) {
                spellCheck.triggerSpellCheck(inputValue);
            }

            if (inputValue?.length > 0) {
                throttledSendTyping();
            }
        },
        [debouncedSetInput, spellCheckEnabled, spellCheck, throttledSendTyping],
    );

    const handleBlur = useCallback(() => {
        debouncedSetInput.flush();
    }, [debouncedSetInput]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                // Important to respect isSendDisabled guard before allowing submit
                if (!isSendDisabled) {
                    void spellCheck.handleSubmitMessage();
                }
            }
        },
        [spellCheck, isSendDisabled],
    );

    return (
        <MessageInputSection
            value={localInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onSubmit={() => void spellCheck.handleSubmitMessage()}
            isSendDisabled={isSendDisabled}
            isInputDisabled={isInputDisabled}
            isSending={isSendingMessage}
            spellCheckStatus={{
                status: spellCheck.displayStatus,
                result: spellCheck.spellCheckState?.result,
                errorMessage: spellCheck.spellCheckState?.errorMessage,
                successDisplayDurationMs: SPELLCHECK_CONFIG.successDisplayMs,
                errorDisplayDurationMs: SPELLCHECK_CONFIG.errorDisplayMs,
                allowSendOriginal: SPELLCHECK_CONFIG.allowSendOriginal,
                onCancel: spellCheck.handleCancelSpellCheck,
                onSendWithChanges: spellCheck.handleSendWithChanges,
                onSendOriginal: spellCheck.handleSendOriginal,
                onDismiss: spellCheck.handleDismissStatus,
            }}
            containsPotentialPii={containsPotentialPii}
            textareaRef={textareaRef}
        />
    );
}
